import { isNonEmptyNumber } from '@labrav/common'
import { isEmpty } from 'lodash'
import {
  awsReferralFieldToFormMetadataKey,
  type Installments,
  type PrivateOffer,
} from '../../types'
import type { MarketplaceFieldMappingInstallments } from '../../../../types/mappingManager'

export const getFilteredInstallments = (installments: Installments[]) => {
  return installments.filter(installment => {
    if (
      installment.amount !== null &&
      isNonEmptyNumber(installment.amount) &&
      !isEmpty(installment.paymentDate)
    ) {
      return installment
    }
  })
}

/** Renewal schedule rows use monthsAfter + % (paymentDate may be empty). */
export const getFilteredRenewalInstallments = (
  installments: Installments[]
) => {
  return installments.filter(installment => {
    if (
      installment.amount !== null &&
      isNonEmptyNumber(installment.amount) &&
      !isEmpty(installment.paymentDate)
    ) {
      return true
    }
    return (
      installment.monthsAfter != null &&
      Number.isFinite(Number(installment.monthsAfter)) &&
      installment.percentage != null &&
      isNonEmptyNumber(installment.percentage)
    )
  })
}

const numOrNull = (value: unknown) => {
  if (value === null || value === undefined || value === '') {
    return null
  }
  const n = Number(value)
  return Number.isFinite(n) ? n : null
}

const PRICE_UPLIFT_TYPE: Record<string, string> = {
  RANGE: 'percentage_range',
  FIXED: 'fixed_percentage',
}

/** Always send the full renewal payload; missing form values → null. */
export const applyAwsRenewalTermsToPayload = (
  offer: PrivateOffer,
  payload: Record<string, unknown>
): Record<string, unknown> => {
  const {
    renewalInstallments: _renewalInstallments,
    renewalFlexiblePaymentType: _renewalFlexiblePaymentType,
    renewalFirstInstallmentDate: _renewalFirstInstallmentDate,
    renewalDayOfMonth: _renewalDayOfMonth,
    renewalPricingTerms: _renewalPricingTerms,
    renewalPricingTerm: _renewalPricingTerm,
    renewalCountType: _renewalCountType,
    renewalCount: _renewalCount,
    optOutDeadlineDays: _optOutDeadlineDays,
    priceUpliftType: _priceUpliftType,
    priceUpliftPercent: _priceUpliftPercent,
    priceUpliftMinPercent: _priceUpliftMinPercent,
    priceUpliftMaxPercent: _priceUpliftMaxPercent,
    adjustmentDeadlineDays: _adjustmentDeadlineDays,
    defaultUpliftPercent: _defaultUpliftPercent,
    renewalInstallmentInfo: _renewalInstallmentInfo,
    ...rest
  } = payload

  const terms = offer.renewalPricingTerms || null
  const priceUpliftType = PRICE_UPLIFT_TYPE[offer.priceUpliftType || ''] ?? null
  const renewalInstallments = getFilteredRenewalInstallments(
    offer.renewalInstallments || []
  )
    .filter(inst => inst.monthsAfter != null && inst.percentage != null)
    .map(inst => ({
      monthsAfterContractStart: Number(inst.monthsAfter),
      dayOfMonth: Number(inst.dayOfMonth ?? 1),
      percentageOfTcv: Number(inst.percentage),
    }))

  rest.autoRenew = Boolean(terms && terms !== 'NO_RENEWAL')
  rest.renewalPricingTerm = terms ? terms.toLowerCase() : null
  rest.renewalPricingConfig = {
    renewalCount: offer.renewalCountType
      ? offer.renewalCountType.toLowerCase()
      : null,
    numberOfRenewals: numOrNull(offer.renewalCount),
    optOutDeadlineDays: numOrNull(offer.optOutDeadlineDays),
    adjustmentDeadlineDays: numOrNull(offer.adjustmentDeadlineDays),
    priceUpliftType,
    uplift:
      priceUpliftType === 'percentage_range'
        ? {
            minimum: numOrNull(offer.priceUpliftMinPercent),
            maximum: numOrNull(offer.priceUpliftMaxPercent),
            default: numOrNull(offer.defaultUpliftPercent),
          }
        : priceUpliftType === 'fixed_percentage'
          ? { percentage: numOrNull(offer.priceUpliftPercent) }
          : null,
    installmentType: offer.renewalFlexiblePaymentType ?? null,
    renewalInstallments:
      renewalInstallments.length > 0 ? renewalInstallments : null,
  }
  return rest
}

/** Normalize API renewal fields onto the offer used by form + detail UI. */
export const normalizeAwsRenewalTermsFromApi = (
  offer: Record<string, unknown>
): Record<string, unknown> => {
  try {
    const { renewalPricingConfig, ...rest } = offer
    const cfg = renewalPricingConfig as Record<string, unknown> | undefined
    const uplift = (cfg?.uplift ?? {}) as Record<string, unknown>
    const beType = String(cfg?.priceUpliftType || '').toLowerCase()
    const nestedRows = Array.isArray(cfg?.renewalInstallments)
      ? (cfg.renewalInstallments as Record<string, unknown>[])
      : undefined
    const toFormEnum = (value: unknown) =>
      typeof value === 'string' ? value.toUpperCase() : value
    const fromConfig: Record<string, unknown> = cfg
      ? {
          ...rest,
          renewalPricingTerms: toFormEnum(
            rest.renewalPricingTerm ??
              cfg.renewalPricingTerm ??
              rest.renewalPricingTerms
          ),
          renewalCountType: toFormEnum(
            cfg.renewalCount ?? rest.renewalCountType
          ),
          renewalCount:
            cfg.numberOfRenewals ??
            (typeof rest.renewalCount === 'number'
              ? rest.renewalCount
              : undefined),
          optOutDeadlineDays: cfg.optOutDeadlineDays ?? rest.optOutDeadlineDays,
          adjustmentDeadlineDays:
            cfg.adjustmentDeadlineDays ?? rest.adjustmentDeadlineDays,
          priceUpliftType:
            beType === 'percentage_range' || beType === 'range'
              ? 'RANGE'
              : beType === 'fixed_percentage' ||
                  beType === 'percentage' ||
                  beType === 'fixed'
                ? 'FIXED'
                : rest.priceUpliftType,
          priceUpliftPercent: uplift.percentage ?? rest.priceUpliftPercent,
          priceUpliftMinPercent: uplift.minimum ?? rest.priceUpliftMinPercent,
          priceUpliftMaxPercent: uplift.maximum ?? rest.priceUpliftMaxPercent,
          defaultUpliftPercent: uplift.default ?? rest.defaultUpliftPercent,
          renewalFlexiblePaymentType:
            cfg.installmentType ?? rest.renewalFlexiblePaymentType,
          renewalInstallments:
            nestedRows && nestedRows.length > 0
              ? nestedRows.map(row => ({
                  monthsAfter: row.monthsAfterContractStart ?? row.monthsAfter,
                  dayOfMonth: row.dayOfMonth,
                  percentage: row.percentageOfTcv ?? row.percentage,
                  paymentDate: (row.paymentDate as string) || '',
                  amount: (row.amount as number | null) ?? null,
                  currency: row.currency || rest.currency,
                }))
              : rest.renewalInstallments,
        }
      : {
          ...rest,
          renewalPricingTerms: toFormEnum(
            rest.renewalPricingTerm ?? rest.renewalPricingTerms
          ),
        }

    const installmentInfo = fromConfig.installmentInfo as
      | Installments[]
      | undefined
    const installments = fromConfig.installments as Installments[] | undefined
    const renewalInstallmentInfo = fromConfig.renewalInstallmentInfo as
      | Installments[]
      | undefined
    const renewalInstallments = fromConfig.renewalInstallments as
      | Installments[]
      | undefined

    return {
      ...fromConfig,
      installments:
        Array.isArray(installments) && installments.length > 0
          ? installments
          : Array.isArray(installmentInfo)
            ? installmentInfo
            : installments,
      renewalInstallments:
        Array.isArray(renewalInstallments) && renewalInstallments.length > 0
          ? renewalInstallments
          : Array.isArray(renewalInstallmentInfo)
            ? renewalInstallmentInfo
            : renewalInstallments,
      renewalDayOfMonth:
        fromConfig.renewalDayOfMonth ??
        (Array.isArray(renewalInstallments) && renewalInstallments[0]
          ? renewalInstallments[0]
          : Array.isArray(renewalInstallmentInfo) && renewalInstallmentInfo[0]
            ? renewalInstallmentInfo[0]
            : undefined
        )?.dayOfMonth,
    }
  } catch {
    return offer
  }
}

/** Resolves installment mapping rows to stable form keys (matches template labels / referral fields). */
export const installmentRowToFormKeySnake = (
  inst: MarketplaceFieldMappingInstallments,
  mappingObjectKey: string
): string => {
  const rf = inst.referralField?.trim() ?? ''
  if (rf) {
    const fromReferral = awsReferralFieldToFormMetadataKey[rf]
    if (fromReferral) {
      return fromReferral
    }
  }
  const label = (inst.label ?? '').toLowerCase()
  if (label.includes('amount')) {
    return 'installment_amount'
  }
  if (label.includes('date')) {
    return 'installment_date'
  }
  return mappingObjectKey
}
